// /home/heliowallet/frontend/components/wallet/SendTransaction.tsx
'use client';

import { useState, useMemo, useEffect, useCallback } from 'react';
import { 
  useAccount, 
  useFeeData, 
  useBalance, 
  useGasPrice, 
  usePublicClient,
  useSendTransaction,
  useWriteContract,
  useWaitForTransactionReceipt,
  useEstimateGas
} from 'wagmi';
import { 
  parseEther, 
  parseUnits, 
  formatUnits, 
  formatGwei, 
  isAddress,
  type Address 
} from 'viem';
import { useWallet } from '@/hooks/useWallet';
import FallbackImage from '@/components/FallbackImage';
import Portal from '@/components/Portal';
import toast from 'react-hot-toast';
import {
  PaperAirplaneIcon,
  ChevronDownIcon,
  FireIcon,
  ClockIcon,
  BoltIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  CogIcon,
  WalletIcon,
  ArrowsRightLeftIcon,
  CalculatorIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { useSelectedRpc } from '@/hooks/useSelectedRpc';
import { useRpcPatcher } from '@/hooks/useRpcPatcher';

// Types
interface Token {
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  balance?: string;
  logoURI?: string;
  isNative?: boolean;
}

interface SendTransactionProps {
  balance: any;
  tokens: Token[];
  chainId: number | undefined;
  onSend: (to: string, value: string) => Promise<void>;
  onSendToken: (tokenAddress: string, to: string, amount: string, decimals: number) => Promise<void>;
  selectedToken?: Token | null;
}

interface GasOption {
  key: 'slow' | 'standard' | 'fast' | 'instant';
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  time: string;
  multiplier: number;
}

interface ValidationError {
  recipient?: string;
  amount?: string;
  gas?: string;
}

// Constants
const NATIVE_GAS_LIMIT = BigInt(21000);
const TOKEN_GAS_LIMIT_BASE = BigInt(65000);
const GAS_BUFFER_PERCENT = 30; // 30% buffer for token transfers
const TEST_ADDRESS = '0xd8dA6BF26964aF9D7eEd9e03E53415D37aA96045'; // vitalik.eth

const GAS_OPTIONS: GasOption[] = [
  { key: 'slow', label: 'Slow', icon: ClockIcon, color: 'text-gray-600', time: '~10 min', multiplier: 0.8 },
  { key: 'standard', label: 'Standard', icon: BoltIcon, color: 'text-blue-600', time: '~3 min', multiplier: 1 },
  { key: 'fast', label: 'Fast', icon: FireIcon, color: 'text-orange-600', time: '~1 min', multiplier: 1.2 },
  { key: 'instant', label: 'Instant', icon: FireIcon, color: 'text-red-600', time: '< 30s', multiplier: 1.5 }
];

const TABS = ['send', 'swap'] as const;
type TabType = typeof TABS[number];

// ERC20 ABI for transfer
const ERC20_ABI = [
  {
    name: 'transfer',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'to', type: 'address' },
      { name: 'amount', type: 'uint256' }
    ],
    outputs: [{ name: '', type: 'bool' }]
  },
  {
    name: 'balanceOf',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ name: '', type: 'uint256' }]
  }
] as const;

export default function SendTransaction({
  balance,
  tokens: propTokens, // Rename to avoid confusion
  chainId,
  onSend,
  onSendToken,
  selectedToken
}: SendTransactionProps) {
  const { address } = useAccount();
  const { sendTransaction } = useSendTransaction();
  const publicClient = usePublicClient();
  
  // Use selected RPC
  const { publicClient: customPublicClient, selectedRpc } = useSelectedRpc(chainId);
  
  // Patch wagmi transport when RPC changes
  useRpcPatcher(chainId, selectedRpc || undefined);
  
  // Get wallet data for prices and utilities
  const { 
    nativeTokenPrice, 
    chainInfo, 
    getExactNativeBalance, 
    getExactTokenBalance,
    tokenPrices,
    tokens: walletTokens
  } = useWallet();

  // Use tokens from useWallet instead of props to ensure real-time updates
  const tokens = walletTokens || [];

  // Gas data - Wagmi should use the selected RPC via useRpcPatcher
  const { data: feeData, isLoading: isLoadingGas, refetch: refetchGas } = useFeeData({ 
    chainId
  });
  const { data: gasPrice } = useGasPrice({ 
    chainId
  });
  
  console.log(`⛽ [SendTransaction] Selected RPC: ${selectedRpc}`);

  // State
  const [activeTab, setActiveTab] = useState<TabType>('send');
  const [selectedAsset, setSelectedAsset] = useState<'native' | string>('native');
  const [recipientAddress, setRecipientAddress] = useState('');
  const [amount, setAmount] = useState('');
  const [gasSpeed, setGasSpeed] = useState<GasOption['key']>('standard');
  const [showAdvancedGas, setShowAdvancedGas] = useState(false);
  const [customGasPrice, setCustomGasPrice] = useState('');
  const [customGasLimit, setCustomGasLimit] = useState('');
  const [showAssetDropdown, setShowAssetDropdown] = useState(false);
  const [errors, setErrors] = useState<ValidationError>({});
  const [estimatedGasLimit, setEstimatedGasLimit] = useState<bigint>(BigInt(21000));
  const [isEstimatingGas, setIsEstimatingGas] = useState(false);
  const [isGettingMaxAmount, setIsGettingMaxAmount] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false); // Confirmation modal state

  // Wait for transaction receipts
  const { isLoading: isConfirmingNative } = useWaitForTransactionReceipt({
    hash: undefined // sendTxHash
  });

  const { isLoading: isConfirmingToken } = useWaitForTransactionReceipt({
    hash: undefined // writeTxHash
  });


  
  // Update selected asset when token is selected from TokenList
  useEffect(() => {
    if (selectedToken) {
      if ('isNative' in selectedToken && selectedToken.isNative) {
        setSelectedAsset('native');
      } else {
        setSelectedAsset(selectedToken.address);
      }
      setActiveTab('send');
      setShowAssetDropdown(false);
    }
  }, [selectedToken]);
  
  // Update selected asset when tokens list changes and we have a selected token
  useEffect(() => {
    if (selectedToken && tokens.length > 0) {
      // Check if the selected token exists in the current tokens list
      const tokenExists = tokens.find(t => t.address === selectedToken.address);
      if (tokenExists) {
        setSelectedAsset(selectedToken.address);
      }
    }
  }, [tokens, selectedToken]);
  
  // Listen for token added events
  useEffect(() => {
    const handleTokenAdded = (event: any) => {
      // If a token was added and we have the token details, auto-select it
      if (event.detail && event.detail.token) {
        const newToken = event.detail.token;
        if (newToken.isNative) {
          setSelectedAsset('native');
        } else {
          setSelectedAsset(newToken.address);
        }
        setActiveTab('send');
        
        // If dropdown is open, close and reopen to show new token
        if (showAssetDropdown) {
          setShowAssetDropdown(false);
          setTimeout(() => {
            setShowAssetDropdown(true);
          }, 100);
        }
      }
    };
    
    window.addEventListener('token-added', handleTokenAdded);
    return () => window.removeEventListener('token-added', handleTokenAdded);
  }, [showAssetDropdown]);
  
  // Refresh gas price periodically
  useEffect(() => {
    const interval = setInterval(async () => {
      if (customPublicClient) {
        console.log(`⛽ [SendTransaction] Refreshing gas price using selected RPC: ${selectedRpc}`);
        try {
          const [gasPrice, feeHistory] = await Promise.allSettled([
            customPublicClient.getGasPrice(),
            customPublicClient.getFeeHistory({ 
              blockCount: 4, 
              rewardPercentiles: [25, 50, 75] 
            }).catch(() => null)
          ]);
          
          console.log(`⛽ [SendTransaction] Gas refresh result using RPC: ${selectedRpc}`, {
            gasPrice: gasPrice.status === 'fulfilled' ? gasPrice.value : null,
            feeHistory: feeHistory.status === 'fulfilled' ? 'available' : 'failed'
          });
        } catch (error) {
          console.error('Failed to refresh gas price:', error);
        }
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [customPublicClient, selectedRpc]);

  // Computed values
  const currentSelectedToken = useMemo(() => {
    return selectedAsset === 'native' ? null : tokens.find(t => t.address === selectedAsset);
  }, [selectedAsset, tokens]);
  
  const gasOption = useMemo(() => {
    return GAS_OPTIONS.find(opt => opt.key === gasSpeed)!;
  }, [gasSpeed]);

  // Get current gas price in Gwei
  const currentGasPrice = useMemo(() => {
    if (feeData?.maxFeePerGas) {
      return Number(formatGwei(feeData.maxFeePerGas));
    }
    if (feeData?.gasPrice) {
      return Number(formatGwei(feeData.gasPrice));
    }
    if (gasPrice) {
      return Number(formatGwei(gasPrice));
    }
    return 20; // Default fallback
  }, [feeData, gasPrice]);

  // Calculate gas price based on selection
  const calculatedGasPrice = useMemo(() => {
    if (customGasPrice) {
      return parseFloat(customGasPrice);
    }
    return currentGasPrice * gasOption.multiplier;
  }, [currentGasPrice, gasOption, customGasPrice]);

  // Final gas limit
  const finalGasLimit = useMemo(() => {
    if (customGasLimit) {
      return BigInt(customGasLimit);
    }
    return estimatedGasLimit;
  }, [customGasLimit, estimatedGasLimit]);

  // Calculate transaction cost
  const transactionCost = useMemo(() => {
    const totalWei = parseUnits(calculatedGasPrice.toString(), 9) * finalGasLimit;
    const costInEth = formatUnits(totalWei, 18);
    const nativePrice = nativeTokenPrice || 0;
    
    return {
      eth: costInEth,
      usd: nativePrice > 0 ? parseFloat(costInEth) * nativePrice : null
    };
  }, [calculatedGasPrice, finalGasLimit, nativeTokenPrice]);

  // Asset info
  const assetInfo = useMemo(() => {
    if (selectedAsset === 'native') {
      return {
        balance: balance?.formatted || '0',
        symbol: balance?.symbol || chainInfo?.nativeCurrency?.symbol || 'ETH',
        decimals: balance?.decimals || 18,
        icon: chainInfo?.icon
      };
    }
    
    const token = currentSelectedToken;
    return {
      balance: token?.balance || '0',
      symbol: token?.symbol || '',
      decimals: token?.decimals || 18,
      icon: token?.logoURI
    };
  }, [selectedAsset, balance, chainInfo, currentSelectedToken]);

  // Estimate gas for token transfers
  const estimateTokenGasLimit = useCallback(async () => {
    if (!currentSelectedToken || !address || !publicClient) return;
    
    setIsEstimatingGas(true);
    try {
      // Get token balance to simulate max transfer
      const tokenBalance = await publicClient.readContract({
        address: currentSelectedToken.address as Address,
        abi: ERC20_ABI,
        functionName: 'balanceOf',
        args: [address as Address]
      }).catch((error) => {
        console.warn('Failed to read token balance:', error);
        return BigInt(0);
      });
      
      if (tokenBalance && tokenBalance > BigInt(0)) {
        // Estimate gas with full balance transfer to test address
        const estimatedGas = await publicClient.estimateContractGas({
          address: currentSelectedToken.address as Address,
          abi: ERC20_ABI,
          functionName: 'transfer',
          args: [TEST_ADDRESS as Address, tokenBalance],
          account: address
        }).catch((error) => {
          console.warn('Failed to estimate gas:', error);
          return TOKEN_GAS_LIMIT_BASE;
        });
        
        // Add buffer (30% more)
        const gasWithBuffer = (estimatedGas * BigInt(100 + GAS_BUFFER_PERCENT)) / BigInt(100);
        setEstimatedGasLimit(gasWithBuffer);
      } else {
        // If no balance or failed to read, use default
        setEstimatedGasLimit(TOKEN_GAS_LIMIT_BASE);
      }
      
    } catch (error) {
      console.warn('Gas estimation failed:', error);
      // Use safe default with buffer
      const defaultWithBuffer = (TOKEN_GAS_LIMIT_BASE * BigInt(100 + GAS_BUFFER_PERCENT)) / BigInt(100);
      setEstimatedGasLimit(defaultWithBuffer);
    } finally {
      setIsEstimatingGas(false);
    }
  }, [currentSelectedToken, address, publicClient]);

  // Estimate gas when inputs change
  useEffect(() => {
    if (selectedAsset === 'native') {
      setEstimatedGasLimit(NATIVE_GAS_LIMIT);
    } else if (currentSelectedToken) {
      estimateTokenGasLimit();
    }
  }, [selectedAsset, currentSelectedToken, estimateTokenGasLimit]);

  // Validation - REMOVED self-send check
  const validateForm = useCallback((): boolean => {
    const newErrors: ValidationError = {};

    // Validate recipient
    if (!recipientAddress) {
      newErrors.recipient = 'Address required';
    } else if (!isAddress(recipientAddress)) {
      newErrors.recipient = 'Invalid address';
    }
    // REMOVED: self-send validation - users can send to themselves

    // Validate amount
    if (amount !== '') {
      const amountNum = parseFloat(amount);
      
      if (isNaN(amountNum)) {
        newErrors.amount = 'Invalid amount';
      } else if (amountNum < 0) {
        newErrors.amount = 'Amount cannot be negative';
      } else if (amountNum > 0) {
        const balanceNum = parseFloat(assetInfo.balance);
        
        if (amountNum > balanceNum) {
          newErrors.amount = 'Insufficient balance';
        }
        
        // Check if user has enough for gas (native token only)
        if (selectedAsset === 'native') {
          const totalNeeded = amountNum + parseFloat(transactionCost.eth);
          if (totalNeeded > balanceNum) {
            newErrors.amount = 'Insufficient balance for amount + gas';
          }
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [recipientAddress, amount, assetInfo.balance, selectedAsset, transactionCost.eth]);

  // Send transaction
  const handleSend = useCallback(async () => {
    if (!validateForm()) return;
    
    // Show confirmation modal instead of sending directly
    setShowConfirmModal(true);
  }, [validateForm]);
  
  // Confirm and execute transaction
  const handleConfirmSend = useCallback(async () => {
    const finalAmount = amount === '' ? '0' : amount;
    
    try {
      if (selectedAsset === 'native') {
        // Send native token using the onSend prop which handles transaction tracking
        await onSend(recipientAddress, finalAmount);
        
        toast.success('Transaction submitted');
      } else if (currentSelectedToken) {
        // Send ERC20 token using the onSendToken prop which handles transaction tracking
        await onSendToken(
          currentSelectedToken.address,
          recipientAddress,
          finalAmount,
          currentSelectedToken.decimals
        );
        
        toast.success('Token transfer submitted');
      }
      
      // Reset form on success
      setRecipientAddress('');
      setAmount('');
      setErrors({});
      setShowConfirmModal(false);
      
    } catch (error: any) {
      console.error('Send error:', error);
      toast.error(error.message || 'Transaction failed');
      setShowConfirmModal(false);
    }
  }, [
    amount, 
    selectedAsset, 
    onSend, 
    recipientAddress, 
    finalGasLimit, 
    calculatedGasPrice,
    currentSelectedToken,
    onSendToken
  ]);

  // Handle max amount - get exact balance from blockchain
  const handleMaxAmount = useCallback(async () => {
    if (!address || !publicClient) {
      toast.error('Wallet not connected');
      return;
    }

    setIsGettingMaxAmount(true);
    
    try {
      if (selectedAsset === 'native') {
        // Get exact native balance
        const exactBalance = await getExactNativeBalance();
        
        // Calculate gas cost
        const gasCostWei = parseUnits(calculatedGasPrice.toString(), 9) * finalGasLimit;
        const gasCostEth = formatUnits(gasCostWei, 18);
        
        // Calculate max amount (balance - gas)
        const balanceNum = parseFloat(exactBalance);
        const gasCostNum = parseFloat(gasCostEth);
        const maxAmount = Math.max(0, balanceNum - gasCostNum);
        
        // Format to avoid scientific notation and ensure precision
        const formattedAmount = maxAmount.toFixed(18).replace(/\.?0+$/, '');
        setAmount(formattedAmount);
        
        toast.success('Max amount calculated');
      } else if (currentSelectedToken) {
        // Get exact token balance
        const exactBalance = await getExactTokenBalance(
          currentSelectedToken.address,
          currentSelectedToken.decimals
        );
        
        // For tokens, we can send the entire balance
        setAmount(exactBalance);
        toast.success('Max amount set');
      }
    } catch (error) {
      console.error('Failed to get max amount:', error);
      toast.error('Failed to calculate max amount');
    } finally {
      setIsGettingMaxAmount(false);
    }
  }, [
    address,
    publicClient,
    selectedAsset,
    getExactNativeBalance,
    getExactTokenBalance,
    currentSelectedToken,
    calculatedGasPrice,
    finalGasLimit
  ]);

  // Format display values
  const formatGasPrice = (price: number): string => {
    if (price < 0.001) return price.toFixed(6);
    if (price < 0.01) return price.toFixed(4);
    if (price < 1) return price.toFixed(3);
    if (price < 10) return price.toFixed(2);
    if (price < 100) return price.toFixed(1);
    return price.toFixed(0);
  };

  const isSending = false; // isSendingNative || isSendingToken;
  const isConfirming = isConfirmingNative || isConfirmingToken;

  // Check insufficient balance
  const isInsufficientBalance = useMemo(() => {
    if (!amount || amount === '0') return false;
    const amountNum = parseFloat(amount);
    const balanceNum = parseFloat(assetInfo.balance);
    return amountNum > balanceNum;
  }, [amount, assetInfo.balance]);
  
  // Check insufficient gas
  const isInsufficientGas = useMemo(() => {
    if (selectedAsset !== 'native' || !balance) return false;
    const nativeBalance = parseFloat(balance.formatted);
    const gasCost = parseFloat(transactionCost.eth);
    return nativeBalance < gasCost;
  }, [selectedAsset, balance, transactionCost.eth]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setActiveTab('send')}
          className={`flex-1 px-6 py-4 font-medium transition-colors relative ${
            activeTab === 'send'
              ? 'text-blue-600 dark:text-blue-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <PaperAirplaneIcon className="h-5 w-5" />
            <span>Send</span>
          </div>
          {activeTab === 'send' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400"></div>
          )}
        </button>
        <button
          onClick={() => setActiveTab('swap')}
          className={`flex-1 px-6 py-4 font-medium transition-colors relative ${
            activeTab === 'swap'
              ? 'text-blue-600 dark:text-blue-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <ArrowsRightLeftIcon className="h-5 w-5" />
            <span>Swap</span>
          </div>
          {activeTab === 'swap' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400"></div>
          )}
        </button>
      </div>

      {activeTab === 'send' ? (
        <div className="p-6 space-y-4">
          {/* Asset Selector */}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
              Asset
            </label>
            <div className="relative">
              <button
                onClick={() => setShowAssetDropdown(!showAssetDropdown)}
                className="w-full p-3 bg-gray-50 dark:bg-gray-700 rounded-xl flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <FallbackImage
                    src={assetInfo.icon}
                    alt={assetInfo.symbol}
                    fallback={selectedAsset === 'native' ? 'chain' : 'token'}
                    fallbackText={assetInfo.symbol}
                    className="h-8 w-8 rounded-full"
                  />
                  <div className="text-left">
                    <p className="font-medium text-gray-900 dark:text-white">{assetInfo.symbol}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Balance: {assetInfo.balance ? parseFloat(assetInfo.balance).toFixed(6) : '0.000000'}
                    </p>
                  </div>
                </div>
                <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform ${showAssetDropdown ? 'rotate-180' : ''}`} />
              </button>

              {/* Asset Dropdown */}
              {showAssetDropdown && (
                <div className="absolute z-20 w-full mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  {/* Header with refresh */}
                  <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Select Asset</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // Close and reopen to refresh
                        setShowAssetDropdown(false);
                        setTimeout(() => setShowAssetDropdown(true), 50);
                      }}
                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                      title="Refresh"
                    >
                      <ArrowPathIcon className="h-4 w-4 text-gray-500" />
                    </button>
                  </div>

                  <div className="max-h-64 overflow-y-auto">
                    {/* Native Token */}
                    <button
                      onClick={() => {
                        setSelectedAsset('native');
                        setShowAssetDropdown(false);
                      }}
                      className={`w-full p-3 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-3 ${
                        selectedAsset === 'native' ? 'bg-gray-50 dark:bg-gray-700' : ''
                      }`}
                    >
                      <FallbackImage
                        src={chainInfo?.icon}
                        alt={balance?.symbol || 'ETH'}
                        fallback="chain"
                        className="h-8 w-8 rounded-full"
                      />
                      <div className="text-left flex-1">
                        <p className="font-medium text-gray-900 dark:text-white">
                          {balance?.symbol || 'ETH'}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {balance?.formatted || '0'}
                        </p>
                      </div>
                      {selectedAsset === 'native' && <CheckCircleIcon className="h-5 w-5 text-green-500" />}
                    </button>

                    {/* Tokens */}
                    {tokens.length === 0 && (
                      <div className="p-4 text-center">
                        <p className="text-sm text-gray-500 dark:text-gray-400">No tokens added yet</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Add tokens from the token list</p>
                      </div>
                    )}
                    {tokens.map(token => (
                      <button
                        key={token.address}
                        onClick={() => {
                          setSelectedAsset(token.address);
                          setShowAssetDropdown(false);
                        }}
                        className={`w-full p-3 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-3 ${
                          selectedAsset === token.address ? 'bg-gray-50 dark:bg-gray-700' : ''
                        }`}
                      >
                        <FallbackImage
                          src={token.logoURI}
                          alt={token.symbol}
                          fallback="token"
                          fallbackText={token.symbol}
                          className="h-8 w-8 rounded-full"
                        />
                        <div className="text-left flex-1">
                          <p className="font-medium text-gray-900 dark:text-white">
                            {token.symbol}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {token.balance || '0.000000'}
                          </p>
                        </div>
                        {selectedAsset === token.address && <CheckCircleIcon className="h-5 w-5 text-green-500" />}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Recipient */}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
              Recipient Address
            </label>
            <div className="relative">
              <input
                type="text"
                value={recipientAddress}
                onChange={(e) => setRecipientAddress(e.target.value)}
                placeholder="0x..."
                className={`w-full px-3 sm:px-4 py-3 pr-14 sm:pr-20 bg-gray-50 dark:bg-gray-700 rounded-xl border-2 transition-colors text-sm sm:text-base ${
                  errors.recipient 
                    ? 'border-red-500 focus:border-red-500' 
                    : 'border-transparent focus:border-blue-500'
                } focus:outline-none`}
              />
              {!recipientAddress && (
                <button
                  onClick={async () => {
                    try {
                      const text = await navigator.clipboard.readText();
                      if (text && isAddress(text)) {
                        setRecipientAddress(text);
                        toast.success('Address pasted');
                      } else {
                        toast.error('Invalid address in clipboard');
                      }
                    } catch (err) {
                      toast.error('Failed to read clipboard');
                    }
                  }}
                  className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 px-2 sm:px-3 py-1 bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-500 text-xs sm:text-sm font-medium"
                >
                  Paste
                </button>
              )}
            </div>
            {errors.recipient && (
              <p className="text-sm text-red-500 mt-1">{errors.recipient}</p>
            )}
          </div>

          {/* Amount */}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
              Amount
            </label>
            <div className="relative">
              <input
                type="text"
                value={amount}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^\d*\.?\d*$/.test(value)) {
                    setAmount(value);
                  }
                }}
                placeholder="0.0"
                className={`w-full px-3 sm:px-4 py-3 pr-14 sm:pr-20 bg-gray-50 dark:bg-gray-700 rounded-xl border-2 transition-colors text-sm sm:text-base ${
                  errors.amount || isInsufficientBalance
                    ? 'border-red-500 focus:border-red-500' 
                    : 'border-transparent focus:border-blue-500'
                } focus:outline-none`}
              />
              <button
                onClick={handleMaxAmount}
                disabled={isGettingMaxAmount || !address}
                className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 px-2 sm:px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800 text-xs sm:text-sm font-medium disabled:opacity-50"
              >
                {isGettingMaxAmount ? (
                  <ArrowPathIcon className="h-3 w-3 animate-spin" />
                ) : (
                  'MAX'
                )}
              </button>
            </div>
            {errors.amount && (
              <p className="text-sm text-red-500 mt-1">{errors.amount}</p>
            )}
            {isInsufficientBalance && !errors.amount && (
              <p className="text-sm text-red-500 mt-1">
                {selectedAsset === 'native' ? 'Insufficient balance for amount + gas' : 'Insufficient balance'}
              </p>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Leave empty to send 0 {assetInfo.symbol}
            </p>
          </div>

          {/* Gas Fee Selector */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Network Fee
              </label>
              <button
                onClick={() => setShowAdvancedGas(!showAdvancedGas)}
                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-1"
              >
                <CogIcon className="h-4 w-4" />
                Advanced
              </button>
            </div>

            {/* Gas Speed Options */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
              {GAS_OPTIONS.map((option) => {
                const Icon = option.icon;
                const isSelected = gasSpeed === option.key;
                
                return (
                  <button
                    key={option.key}
                    onClick={() => setGasSpeed(option.key)}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <Icon className={`h-5 w-5 ${option.color} mx-auto mb-1`} />
                    <p className="text-xs font-medium">{option.label}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{option.time}</p>
                  </button>
                );
              })}
            </div>

            {/* Gas Price Display */}
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-xl">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex-1">
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Gas Price</p>
                  <div className="flex items-center gap-2">
                    <p className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                      {formatGasPrice(calculatedGasPrice)} Gwei
                    </p>
                    {isEstimatingGas && selectedAsset !== 'native' && (
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <ArrowPathIcon className="h-3 w-3 animate-spin" />
                        <span className="hidden sm:inline">Estimating...</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 sm:gap-3 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
                    {!customGasPrice && (
                      <span>Base: {formatGasPrice(currentGasPrice)} Gwei</span>
                    )}
                    <span className="hidden sm:inline">•</span>
                    <span>Gas Limit: {finalGasLimit.toString()}</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Estimated Fee</p>
                  <p className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                    {transactionCost.usd !== null ? `$${transactionCost.usd.toFixed(2)}` : '---'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {parseFloat(transactionCost.eth).toFixed(6)} {balance?.symbol}
                  </p>
                </div>
              </div>
            </div>

            {/* Advanced Gas Settings */}
            {showAdvancedGas && (
              <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-xl space-y-3">
                <div>
                  <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">
                    Gas Price (Gwei)
                  </label>
                  <input
                    type="number"
                    value={customGasPrice}
                    onChange={(e) => setCustomGasPrice(e.target.value)}
                    placeholder={formatGasPrice(calculatedGasPrice)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-600 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">
                    Gas Limit
                  </label>
                  <input
                    type="number"
                    value={customGasLimit}
                    onChange={(e) => setCustomGasLimit(e.target.value)}
                    placeholder={finalGasLimit.toString()}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-600 rounded-lg text-sm"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Transaction Summary */}
          {(amount || recipientAddress) && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
              <div className="flex items-start gap-3">
                <InformationCircleIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                    Transaction Summary
                  </h4>
                  <div className="space-y-1 text-sm">
                    {recipientAddress && (
                      <div className="flex justify-between">
                        <span className="text-blue-700 dark:text-blue-300">To:</span>
                        <span className="font-mono text-blue-800 dark:text-blue-200">
                          {recipientAddress.slice(0, 6)}...{recipientAddress.slice(-4)}
                        </span>
                      </div>
                    )}
                    {amount && (
                      <div className="flex justify-between">
                        <span className="text-blue-700 dark:text-blue-300">Amount:</span>
                        <span className="font-medium text-blue-800 dark:text-blue-200">
                          {amount} {assetInfo.symbol}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-blue-700 dark:text-blue-300">Network Fee:</span>
                      <span className="font-medium text-blue-800 dark:text-blue-200">
                        ~{parseFloat(transactionCost.eth).toFixed(6)} {balance?.symbol}
                      </span>
                    </div>
                    {selectedAsset === 'native' && amount && (
                      <div className="flex justify-between pt-2 border-t border-blue-200 dark:border-blue-800">
                        <span className="text-blue-700 dark:text-blue-300">Total:</span>
                        <span className="font-bold text-blue-800 dark:text-blue-200">
                          {(parseFloat(amount || '0') + parseFloat(transactionCost.eth)).toFixed(6)} {balance?.symbol}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Insufficient Gas Warning for Token Transfers */}
          {selectedAsset !== 'native' && isInsufficientGas && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-xl flex gap-2">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-700 dark:text-red-300">
                Insufficient {balance?.symbol || 'ETH'} for gas fees. You need at least {parseFloat(transactionCost.eth).toFixed(6)} {balance?.symbol} for network fees.
              </p>
            </div>
          )}
          
          {/* High Amount Warning */}
          {amount && parseFloat(amount) > 0 && parseFloat(amount) > parseFloat(assetInfo.balance) * 0.9 && (
            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl flex gap-2">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0" />
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                You're sending over 90% of your balance. Make sure to keep some for future transactions.
              </p>
            </div>
          )}

          {/* Send Button */}
          <button
            onClick={handleSend}
            disabled={isSending || isConfirming || !recipientAddress || isLoadingGas || isInsufficientBalance || isInsufficientGas}
            className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-medium hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
          >
            {isSending || isConfirming ? (
              <>
                <ArrowPathIcon className="h-5 w-5 animate-spin" />
                {isConfirming ? 'Confirming...' : 'Sending...'}
              </>
            ) : (
              <>
                <PaperAirplaneIcon className="h-5 w-5" />
                Send {assetInfo.symbol}
              </>
            )}
          </button>
        </div>
      ) : (
        <div className="p-6">
          <div className="text-center py-12">
            <ArrowsRightLeftIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400 text-lg font-medium">
              Swap feature coming soon
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
              Trade tokens directly from your wallet
            </p>
          </div>
        </div>
      )}
      
      {/* Confirmation Modal */}
      {showConfirmModal && (
        <Portal>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-4 sm:p-6 shadow-xl max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <ExclamationTriangleIcon className="h-5 sm:h-6 w-5 sm:w-6 text-yellow-500" />
                Confirm Transaction
              </h3>
              
              <div className="space-y-4">
                {/* Asset & Amount */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">You are sending</p>
                  <div className="flex items-center gap-3">
                    <FallbackImage
                      src={assetInfo.icon}
                      alt={assetInfo.symbol}
                      fallback={selectedAsset === 'native' ? 'chain' : 'token'}
                      className="h-10 w-10 rounded-full"
                    />
                    <div>
                      <p className="text-xl font-bold text-gray-900 dark:text-white">
                        {amount || '0'} {assetInfo.symbol}
                      </p>
                      {selectedAsset === 'native' && nativeTokenPrice && amount && (
                        <p className="text-sm text-gray-500">
                          ≈ ${(parseFloat(amount) * nativeTokenPrice).toFixed(2)}
                        </p>
                      )}
                      {selectedAsset !== 'native' && currentSelectedToken && tokenPrices[currentSelectedToken.address.toLowerCase()] && amount && (
                        <p className="text-sm text-gray-500">
                          ≈ ${(parseFloat(amount) * tokenPrices[currentSelectedToken.address.toLowerCase()]).toFixed(2)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Recipient */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">To address</p>
                  <p className="font-mono text-sm text-gray-900 dark:text-white break-all">
                    {recipientAddress}
                  </p>
                </div>
                
                {/* Gas Fee */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Network fee</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {transactionCost.usd !== null ? `$${transactionCost.usd.toFixed(2)}` : '---'}
                    </p>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{formatGasPrice(calculatedGasPrice)} Gwei</span>
                    <span>{parseFloat(transactionCost.eth).toFixed(6)} {balance?.symbol}</span>
                  </div>
                </div>
                
                {/* Total */}
                {selectedAsset === 'native' && (
                  <div className="border-t dark:border-gray-700 pt-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Total (amount + fee)</p>
                      <div className="text-right">
                        <p className="font-bold text-gray-900 dark:text-white">
                          {(parseFloat(amount || '0') + parseFloat(transactionCost.eth)).toFixed(6)} {balance?.symbol}
                        </p>
                        {transactionCost.usd !== null && nativeTokenPrice && (
                          <p className="text-sm text-gray-500">
                            ≈ ${((parseFloat(amount || '0') + parseFloat(transactionCost.eth)) * nativeTokenPrice).toFixed(2)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Actions */}
              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="flex-1 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmSend}
                  disabled={isSending}
                  className="flex-1 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-medium hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                >
                  {isSending ? (
                    <>
                      <ArrowPathIcon className="h-5 w-5 animate-spin" />
                      Confirming...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="h-5 w-5" />
                      Confirm
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
}